import { ClientReadable, clientsListOptions } from '@crm/api'
import { ClientFilters } from '@crm/features/clients/components/ClientFilters'
import { useAppNavigate } from '@crm/hooks/useAppNavigate'
import { useTableSorter } from '@crm/hooks/useTableSorter'
import { paths } from '@crm/routes'
import {
  ConditionalText,
  DayLabel,
  TableContainer,
  TitleSection
} from '@shared/components'
import { useQueryUpdate } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { defaultFilters } from '@shared/utils'
import { useQuery } from '@tanstack/react-query'

import { useMemo } from 'react'

import type { TableColumnsType } from 'antd'

export const Clients = () => {
  const { t } = useLocales()
  const { params } = useQueryUpdate()
  const sorter = useTableSorter()
  const navigate = useAppNavigate()

  const { data: customers, isLoading } = useQuery(
    clientsListOptions({
      query: {
        ...defaultFilters,
        ...params
      }
    })
  )

  const columns: TableColumnsType<ClientReadable> = useMemo(
    () => [
      {
        title: t('contacts.contact_name'),
        key: 'name',
        sorter: true,
        render: (_, { name }) => (
          <ConditionalText condition={!!name}>{name}</ConditionalText>
        )
      },
      {
        title: t('contacts.status'),
        key: 'status',
        render: (_, { status }) => (
          <ConditionalText condition={!!status?.name}>
            {status?.name}
          </ConditionalText>
        )
      },
      {
        title: t('contacts.name_lead'),
        key: 'primary_contact',
        render: (_, { primary_contact }) => (
          <ConditionalText condition={!!primary_contact?.first_name}>
            {[primary_contact?.first_name, primary_contact?.last_name]
              .filter(Boolean)
              .join(' ')}
          </ConditionalText>
        )
      },
      {
        title: t('identification_number'),
        key: 'identification_number',
        render: (_, { identification_number }) => (
          <ConditionalText condition={!!identification_number}>
            {identification_number}
          </ConditionalText>
        )
      },
      {
        title: t('created_by'),
        key: 'created_by',
        render: (_, { created_by }) => (
          <ConditionalText
            condition={!!(created_by?.first_name || created_by?.last_name)}
          >
            {[created_by?.first_name, created_by?.last_name]
              .filter(Boolean)
              .join(' ')}
          </ConditionalText>
        )
      },
      {
        title: t('created_at'),
        key: 'created_at',
        sorter: true,
        render: (_, { created_at }) => <DayLabel date={created_at} />
      }
    ],
    [t]
  )

  return (
    <>
      <TitleSection
        title={t(`menu.customers`)}
        count={customers?.count}
        isLoading={isLoading}
      />

      <TableContainer
        columns={columns}
        isHoverAlternative
        rowKey="id"
        data={customers}
        loading={isLoading}
        showSearch
        leftChildren={<ClientFilters />}
        onChange={(_, __, sorterResult) => sorter(sorterResult)}
        rowClassName="pointer"
        showSorterTooltip={false}
        onRow={(record: ClientReadable) => ({
          onClick: () => navigate(paths.clientsDetails.root(String(record.id)))
        })}
      />
    </>
  )
}
